<template>
  <div :id="mapName" class="h-screen w-full"></div>
</template>

<script>
import {find} from "lodash";
import mapStyle from '@/constants/mapStyle.json';
import { SensorMarker } from '@/models/SensorMarker';
import { TargetMarker } from "@/models/TargetMarker";
import { mapMutations, mapGetters } from 'vuex';

const DEFAULT_ZOOM = 7;

export default {
  name: 'Googlemap',

  props: {
    centerLatitude: {
      type: Number,
      default: 0,
    },
    centerLongitude: {
      type: Number,
      default: 0,
    },
    markers: {
      type: Array,
      default() {
        return [{ latitude: 0, longitude: 0 }];
      },
    },
  },

  watch: {
    '$route.params.id'() {
      this.setTargetMarker();
    },
    markers() {
      this.setMarkers();
    },
  },

  computed: {
    ...mapGetters([
      'boundaries',
    ]),
  },

  data() {
    return {
      mapName: 'sensors-map',
      map: null,
      zoom: DEFAULT_ZOOM,
      markersList: [],
      isMapIdle: true,
      boundsTimeout: null,
    };
  },

  mounted() {
    this.setMap();
  },

  methods: {
    ...mapMutations([
      'setBoundaries',
    ]),

    setMap() {
      const { mapName, zoom } = this;
      const gmaps = window.google.maps;

      this.map =  new gmaps.Map(
        document.getElementById(mapName),
        {
          center: new gmaps.LatLng(this.centerLatitude, this.centerLongitude),
          zoom,
          styles: mapStyle,
          fullscreenControl: false,
        },
      );

      gmaps.event.addListener(
        this.map,
        'idle',
        this.hasBoundsExtended,
      );

      this.setMarkers();
    },

    setMarkers() {
      const { map, markers, showSensorInfo } = this;
      const prevMarkers = this.markersList;

      this.markersList = [];
      markers && markers.forEach(m => this.markersList.push(
        new SensorMarker(m, map, () => showSensorInfo(m.id)),
      ));
      setTimeout(() => prevMarkers.forEach(m => m.clear()), 100);
      this.setTargetMarker();
    },

    setTargetMarker(){
      this.targetMarker && this.targetMarker.clear();
      this.targetMarker = null;
      if(this.$route.params.id) {
        const {map, markers} = this;
        const m = find(markers, (m) => {
          return m.id == this.$route.params.id
        });
        if (m) {
          this.targetMarker = new TargetMarker(m, map);
        }
      }
    },

    showSensorInfo(id) {
      const currentId = this.$route.params ? Number(this.$route.params.id) : null;

      if (currentId !== id) {
        this.$router.push({ name: 'sensor', params: { id } });
      }
    },

    getMapBounds(bounds) {
      const { boundaries } = this;
      const southWest = bounds.getSouthWest();
      const northEast = bounds.getNorthEast();
      return {
        N: !!boundaries ? Math.max(northEast.lat(), boundaries.N) : northEast.lat(),
        S: !!boundaries ? Math.min(southWest.lat(), boundaries.S) : southWest.lat(),
        E: !!boundaries ? Math.max(northEast.lng(), boundaries.E) : northEast.lng(),
        W: !!boundaries ? Math.min(southWest.lng(), boundaries.W) : southWest.lng(),
      };
    },

    hasBoundsExtended() {
      if (!this.isMapIdle) {
        return;
      }

      const { boundaries, getMapBounds, map, setBoundaries } = this;
      const newBounds = getMapBounds(map.getBounds());

      clearTimeout(this.boundsTimeout);
      if (!boundaries || Object.entries(newBounds).toString() !== Object.entries(boundaries).toString()) {
        this.boundsTimeout = setTimeout(
          () => {
            setBoundaries(newBounds);
            this.$emit('boundsChanged');
          },
          600,
        );
      }
    },

    centerMap(position) {
      const { map, smoothZoom } = this;

      if (map.getZoom() < 9) {
        smoothZoom(9);
      }
      map.panTo(
        new window.google.maps.LatLng(position.lat, position.lon),
      );
    },

    smoothZoom(endZoom) {
      const { map, smoothZoom } = this;
      this.isMapIdle = map.getZoom() === endZoom;

      if (!this.isMapIdle) {
        const increment = endZoom > map.getZoom() ? 1 : -1;
        map.setZoom(map.getZoom() + increment);
        setTimeout(() => smoothZoom(endZoom), 50);
      } else {
        this.zoom = endZoom;
      }
    },

  },
};
</script>

<style lang="scss" scoped>
.h-screen {
  height: calc(100vh - 4rem);

  @media (min-width: theme("screens.md")) {
    height: calc(100vh - 5rem);
  }
}
</style>
